import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Modal, Box, Typography, Button, CircularProgress, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar,
  ListItemSecondaryAction, Checkbox, TextField, InputAdornment,
  IconButton, Divider, Alert, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import PersonIcon from '@mui/icons-material/Person';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryClient } from '@tanstack/react-query';
import { useUserService, useFilterService } from '../../service';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflow: 'auto'
};

export default function AssignUsersToFilterModal({
  open,
  onClose,
  onUsersAssigned,
  filterId,
  filterName,
  roleId,
  theme
}) {
  // =================== HOOKS DE SERVICIOS (NIVEL SUPERIOR) ===================
  const userService = useUserService();
  const filterService = useFilterService();
  const queryClient = useQueryClient();

  // =================== ESTADO LOCAL ===================
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // =================== ESTADO COMPUTADO ===================
  const filteredUsers = useMemo(() => {
    if (searchTerm.trim() === '') {
      return users;
    }

    const lowercaseSearch = searchTerm.toLowerCase();
    return users.filter(user => {
      const fullName = `${user.first_name} ${user.first_last_name} ${user.second_last_name || ''}`.toLowerCase();
      const documentNumber = user.document_number?.toLowerCase() || '';
      
      return fullName.includes(lowercaseSearch) || documentNumber.includes(lowercaseSearch);
    });
  }, [searchTerm, users]);

  const assignmentStats = useMemo(() => {
    const totalUsers = filteredUsers.length;
    const selectedCount = selectedUsers.length;
    const initiallyAssigned = users.filter(user => user.hasFilter).length;
    const newAssignments = selectedUsers.filter(id => {
      const user = users.find(u => u.id === id);
      return user && !user.hasFilter;
    }).length;
    const removals = users.filter(user => user.hasFilter && !selectedUsers.includes(user.id)).length;

    return {
      totalUsers,
      selectedCount,
      initiallyAssigned,
      newAssignments,
      removals
    };
  }, [filteredUsers, selectedUsers, users]);

  // =================== EFECTOS ===================
  useEffect(() => {
    if (open && filterId && roleId) {
      loadUsers();
    }
    
    // Limpiar estado al cerrar
    if (!open) {
      resetModalState();
    }
  }, [open, filterId, roleId]);

  // Auto-hide success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // =================== FUNCIONES DE CARGA ===================
  const loadUsers = useCallback(async () => {
    if (!roleId) {
      setError('ID de rol no proporcionado');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await userService.getUsersByRoleWithoutPagination(roleId);
      
      if (!response?.data) {
        throw new Error('No se recibieron datos del servidor');
      }
      
      // Procesar usuarios con estado de filtro
      const processedUsers = response.data.map(user => ({
        ...user,
        hasFilter: user.assigned_filters?.some(filter => filter.id === filterId) || false
      }));
      
      setUsers(processedUsers);
      
      // Pre-seleccionar usuarios que ya tienen el filtro
      const preselectedUsers = processedUsers
        .filter(user => user.hasFilter)
        .map(user => user.id);
      
      setSelectedUsers(preselectedUsers);
      
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setError(error.message || 'Error al cargar la lista de usuarios');
    } finally {
      setLoading(false);
    }
  }, [roleId, filterId, userService]);

  // =================== HANDLERS DE INTERACCIÓN ===================
  const handleToggleUser = useCallback((userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allFilteredIds = filteredUsers.map(user => user.id);
    const allSelected = allFilteredIds.every(id => selectedUsers.includes(id));
    
    if (allSelected) {
      // Deseleccionar todos los filtrados, pero mantener los no filtrados que estaban seleccionados
      setSelectedUsers(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Seleccionar todos los filtrados, manteniendo los ya seleccionados
      setSelectedUsers(prev => [...new Set([...prev, ...allFilteredIds])]);
    }
  }, [filteredUsers, selectedUsers]);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  // =================== LÓGICA DE GUARDADO ===================
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    
    try {
      // Validaciones previas
      if (!filterId) {
        throw new Error('ID de filtro no proporcionado');
      }

      // Identificar cambios necesarios
      const initiallyAssigned = users
        .filter(user => user.hasFilter)
        .map(user => user.id);
      
      const toAdd = selectedUsers.filter(id => !initiallyAssigned.includes(id));
      const toRemove = initiallyAssigned.filter(id => !selectedUsers.includes(id));
      
      // Si no hay cambios, cerrar sin hacer nada
      if (toAdd.length === 0 && toRemove.length === 0) {
        setSuccessMessage('No hay cambios que guardar');
        onClose();
        return;
      }

      // Crear operaciones en lotes para mejor rendimiento
      const addOperations = toAdd.map(userId => 
        filterService.assignFilterToUser(userId, filterId)
      );
      
      const removeOperations = toRemove.map(userId => 
        filterService.removeFilterFromUser(userId, filterId)
      );
      
      // Ejecutar operaciones en paralelo con control de errores
      const [addResults, removeResults] = await Promise.allSettled([
        Promise.allSettled(addOperations),
        Promise.allSettled(removeOperations)
      ]);

      // Verificar si hubo errores en las operaciones
      const addFailures = addResults.value?.filter(result => result.status === 'rejected') || [];
      const removeFailures = removeResults.value?.filter(result => result.status === 'rejected') || [];
      
      if (addFailures.length > 0 || removeFailures.length > 0) {
        const errorMessage = `Se completaron las asignaciones con ${addFailures.length + removeFailures.length} errores`;
        console.warn(errorMessage, { addFailures, removeFailures });
        setError(errorMessage);
      } else {
        setSuccessMessage(
          `Asignaciones actualizadas correctamente: ${toAdd.length} agregados, ${toRemove.length} removidos`
        );
      }
      
      // Invalidar consultas para refrescar datos
      await queryClient.invalidateQueries(['usersWithRole', roleId]);
      await queryClient.invalidateQueries(['filterAssignments', filterId]);
      
      // Notificar éxito al componente padre
      if (onUsersAssigned) {
        onUsersAssigned({
          added: toAdd.length,
          removed: toRemove.length,
          total: selectedUsers.length
        });
      }
      
      // Recargar datos para mostrar estado actualizado
      await loadUsers();
      
    } catch (error) {
      console.error('Error al guardar asignaciones:', error);
      setError(error.message || 'Error al guardar las asignaciones');
    } finally {
      setSaving(false);
    }
  }, [
    selectedUsers, users, filterId, filterService, 
    queryClient, roleId, onUsersAssigned, loadUsers, onClose
  ]);

  // =================== FUNCIONES DE UTILIDAD ===================
  const resetModalState = useCallback(() => {
    setUsers([]);
    setSelectedUsers([]);
    setSearchTerm('');
    setError(null);
    setSuccessMessage('');
    setLoading(false);
    setSaving(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!saving) {
      onClose();
    }
  }, [saving, onClose]);

  // =================== RENDERIZADO CONDICIONAL ===================
  const renderUserList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (filteredUsers.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios disponibles'}
          </Typography>
          {searchTerm && (
            <Button 
              size="small" 
              onClick={() => setSearchTerm('')}
              sx={{ mt: 1 }}
            >
              Limpiar búsqueda
            </Button>
          )}
        </Box>
      );
    }

    return (
      <List sx={{ 
        maxHeight: 400, 
        overflow: 'auto', 
        bgcolor: theme === 'dark' ? 'rgba(66, 66, 66, 0.8)' : 'rgba(245, 245, 245, 0.9)',
        borderRadius: 1
      }}>
        {filteredUsers.map((user) => {
          const isSelected = selectedUsers.includes(user.id);
          const hadFilter = user.hasFilter;
          
          return (
            <ListItem 
              key={user.id} 
              divider
              sx={{
                bgcolor: hadFilter ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: hadFilter ? 'primary.main' : 'grey.400' }}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {`${user.first_name} ${user.first_last_name} ${user.second_last_name || ''}`.trim()}
                    </Typography>
                    {hadFilter && (
                      <Chip 
                        size="small" 
                        label="Asignado" 
                        color="primary" 
                        variant="outlined"
                      />
                    )}
                  </Box>
                }
                secondary={`Documento: ${user.document_number}`}
              />
              <ListItemSecondaryAction>
                <Checkbox
                  edge="end"
                  checked={isSelected}
                  onChange={() => handleToggleUser(user.id)}
                  disabled={saving}
                />
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  // =================== RENDERIZADO PRINCIPAL ===================
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-assign-users-to-filter"
    >
      <Box sx={style} className={`GM__${theme}-modal-container`}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2" id="modal-assign-users-to-filter">
            Asignar Operadores al Filtro: {filterName}
          </Typography>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Description */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body1">
            Seleccione los operadores que desea asociar con este filtro.
          </Typography>
        </Box>

        {/* Messages */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <IconButton size="small" onClick={() => setError(null)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {/* Search */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Buscar operadores por nombre o documento..."
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
          disabled={loading || saving}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Stats and Controls */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="body2">
              {assignmentStats.selectedCount} de {assignmentStats.totalUsers} operadores seleccionados
            </Typography>
            {assignmentStats.newAssignments > 0 && (
              <Typography variant="caption" color="success.main">
                +{assignmentStats.newAssignments} nuevas asignaciones
              </Typography>
            )}
            {assignmentStats.removals > 0 && (
              <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                -{assignmentStats.removals} remociones
              </Typography>
            )}
          </Box>
          
          <Button 
            size="small" 
            onClick={handleSelectAll}
            disabled={loading || saving || filteredUsers.length === 0}
          >
            {filteredUsers.every(user => selectedUsers.includes(user.id)) 
              ? 'Deseleccionar visibles' 
              : 'Seleccionar visibles'}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* User List */}
        {renderUserList()}

        {/* Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleClose} 
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving || loading || assignmentStats.totalUsers === 0}
            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}